/**
 * El extracto: el libro mayor del cliente.
 *
 * SE LEE DE ARRIBA HACIA ABAJO Y POR ESO VA ASCENDENTE
 * ----------------------------------------------------
 * La columna que importa es el saldo acumulado, y un acumulado en orden
 * invertido no acumula nada. Va del movimiento mas viejo al mas nuevo, con el
 * saldo de hoy al final.
 *
 * LA TRAMPA DE LAS COLUMNAS
 * -------------------------
 * `v_extracto_clientes` trae el debe y el haber en la moneda DEL COMPROBANTE, y
 * el saldo acumulado en la moneda de ANCLAJE de la cuenta. Poner esas columnas
 * una al lado de la otra es mezclar pesos con dolares en la misma linea: una
 * venta de $ 1.656.000 aparecia junto a un saldo de 1.288,72 y nadie entiende
 * de donde salio ese numero.
 *
 * Aca las tres columnas de plata estan SIEMPRE en la moneda de la cuenta, y el
 * importe original —cuando el comprobante se hizo en la otra moneda— se muestra
 * abajo, con la cotizacion a la que se asento ese dia.
 *
 * El signo de cada movimiento se dice con el signo y una flecha, nunca con el
 * color del numero ni con la barra lateral: esa barra significa moneda, igual
 * que en Caja (MASTER seccion 3.2).
 */
import type { Moneda } from "@tora/shared";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";

import { ChipMoneda, Importe } from "@/components/money";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableHead,
  TableHeader,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { cn } from "@/lib/utils";
import { formatearFecha } from "@/lib/utils";

import { textoCotizacion, type LineaExtracto } from "./logica";

const COLUMNAS = 5;
const MENSAJE_VACIO = "Este cliente todavía no tiene movimientos en cuenta corriente.";

export interface ExtractoProps {
  lineas: ReadonlyArray<LineaExtracto>;
  moneda: Moneda;
  /** Cuantos movimientos hay en total, si hay mas de los que se trajeron. */
  total?: number;
  cargando?: boolean;
  /**
   * Lo cobrado a cuenta de reparaciones que siguen en el taller
   * (`v_saldos_clientes.senas_ot`, 0121_7), en la moneda de la cuenta.
   */
  reservadoTaller?: number;
}

export function Extracto({
  lineas,
  moneda,
  total,
  cargando = false,
  reservadoTaller = 0,
}: ExtractoProps): JSX.Element {
  const vacio = lineas.length === 0;
  const faltan = total !== undefined && total > lineas.length ? total - lineas.length : 0;
  // El extracto asienta la seña de una orden abierta como cualquier cobro,
  // pero el saldo de arriba (v_saldos_clientes) no la descuenta hasta la
  // entrega: la ultima fila y el «Debe» del encabezado no coincidian y nadie
  // decia por que. Solo con el extracto completo la ultima fila es la de hoy.
  const ultimo = !vacio && faltan === 0 ? lineas[lineas.length - 1] : undefined;
  const reservado = reservadoTaller > 0.009 ? reservadoTaller : 0;

  return (
    <section className="space-y-3">
      <TituloSeccion
        acciones={
          <span className="flex flex-wrap items-center gap-1.5 text-xs font-normal text-muted-foreground">
            Las tres columnas de plata están en
            <ChipMoneda moneda={moneda} />
            <span>{moneda === "USD" ? "dólares" : "pesos"}, la moneda de esta cuenta</span>
          </span>
        }
      >
        Movimientos de la cuenta
      </TituloSeccion>

      {/* Desktop: tabla densa. */}
      <Card padding="none" className="hidden overflow-hidden md:block">
        <Table densidad="compacta" alto="acotado">
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Movimiento</TableHead>
              <TableHead className="text-right">Debe</TableHead>
              <TableHead className="text-right">Haber</TableHead>
              <TableHead className="text-right">Saldo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vacio ? (
              cargando ? (
                <tr>
                  <td colSpan={COLUMNAS}>
                    <Cargando texto="Cargando el extracto…" />
                  </td>
                </tr>
              ) : (
                <TableVacia colSpan={COLUMNAS} mensaje={MENSAJE_VACIO} />
              )
            ) : (
              lineas.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                    {formatearFecha(l.fecha)}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant={variantePorTipo(l.tipo)}>{l.etiqueta}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {l.referencia ?? l.detalle}
                      </span>
                    </div>
                    <Original linea={l} />
                  </TableCell>
                  <TableCellImporte>
                    {l.debe > 0.009 ? (
                      <Importe monto={l.debe} moneda={l.moneda} tamano="sm" codigo={false} />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCellImporte>
                  <TableCellImporte>
                    {l.haber > 0.009 ? (
                      <Importe monto={l.haber} moneda={l.moneda} tamano="sm" codigo={false} />
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCellImporte>
                  <TableCellImporte className="font-medium">
                    <Saldo monto={l.saldo} moneda={l.moneda} />
                  </TableCellImporte>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Mobile: una tarjeta-registro por movimiento, con la barra lateral de su moneda. */}
      <div className="flex flex-col gap-2 md:hidden">
        {vacio ? (
          cargando ? (
            <Cargando texto="Cargando el extracto…" />
          ) : (
            <EstadoVacio titulo={MENSAJE_VACIO} />
          )
        ) : (
          lineas.map((l) => (
            <Card
              key={l.id}
              as="article"
              padding="compacto"
              acento={l.moneda}
              className="flex items-start justify-between gap-3"
            >
              <div className="min-w-0 space-y-1">
                <Badge variant={variantePorTipo(l.tipo)}>{l.etiqueta}</Badge>
                <p className="truncate text-xs text-muted-foreground">
                  {formatearFecha(l.fecha)}
                  {l.referencia ? ` · ${l.referencia}` : ""}
                </p>
                <Original linea={l} />
              </div>
              <div className="flex shrink-0 flex-col items-end gap-0.5">
                <ImporteFirmado linea={l} />
                <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
                  saldo <Saldo monto={l.saldo} moneda={l.moneda} />
                </span>
              </div>
            </Card>
          ))
        )}
      </div>

      {reservado > 0 ? (
        <p className="text-xs text-muted-foreground">
          {ultimo ? (
            <>
              Saldo del extracto{" "}
              <Importe monto={ultimo.saldo} moneda={moneda} tamano="sm" codigo={false} /> más{" "}
            </>
          ) : null}
          <Importe monto={reservado} moneda={moneda} tamano="sm" codigo={false} /> reservados para
          reparaciones que siguen en el taller
          {ultimo ? (
            <>
              {" "}
              ={" "}
              <Importe
                monto={ultimo.saldo + reservado}
                moneda={moneda}
                tamano="sm"
                codigo={false}
              />
              , el saldo de arriba
            </>
          ) : null}
          . Esos cobros figuran acá como haber, pero se descuentan recién al entregar el equipo.
        </p>
      ) : null}

      {faltan > 0 ? (
        <p className="text-xs text-muted-foreground">
          Se muestran los {lineas.length} movimientos más viejos. Faltan {faltan} más.
        </p>
      ) : null}
    </section>
  );
}

/**
 * El saldo acumulado. Uno acreedor se escribe «$ 7,90 a favor» y no «-$ 7,90»:
 * la tarjeta de arriba dice «A FAVOR +$ 7,90» y el menos en la columna se leia
 * como si debiera.
 */
function Saldo({ monto, moneda }: { monto: number; moneda: Moneda }): JSX.Element {
  if (monto < -0.009) {
    return (
      <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
        <Importe monto={-monto} moneda={moneda} tamano="sm" codigo={false} />
        <span className="text-xs font-normal text-success">a favor</span>
      </span>
    );
  }
  return <Importe monto={monto} moneda={moneda} tamano="sm" codigo={false} />;
}

/**
 * En la tarjeta mobile las dos columnas se juntan en un importe con signo: el
 * debe suma a la deuda (+) y el haber la baja (−). El signo lo pone Intl y
 * ademas hay una flecha; el numero queda en el color de su moneda.
 */
function ImporteFirmado({ linea }: { linea: LineaExtracto }): JSX.Element {
  const suma = linea.debe > 0.009;
  const Icono = suma ? ArrowUpRight : ArrowDownLeft;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <Icono
        className={cn("size-3.5 shrink-0", suma ? "text-muted-foreground" : "text-success")}
        aria-hidden="true"
      />
      <span className="sr-only">{suma ? "Debe" : "Haber"}</span>
      <Importe
        monto={suma ? linea.debe : -linea.haber}
        moneda={linea.moneda}
        codigo={false}
        signoExplicito
      />
    </span>
  );
}

/**
 * El importe tal como se documento, cuando el comprobante era en la otra moneda.
 *
 * Es la unica forma honesta de mostrar una venta hecha en pesos dentro de una
 * cuenta llevada en dolares: el numero de la cuenta y el numero del papel son
 * distintos, y lo que los une es la cotizacion de ESE dia.
 */
function Original({ linea }: { linea: LineaExtracto }): JSX.Element | null {
  if (!linea.original) return null;
  const { monto, moneda, cotizacion } = linea.original;
  return (
    <p className="mt-0.5 text-xs text-muted-foreground">
      Se documentó en <Importe monto={monto} moneda={moneda} tamano="sm" />
      {cotizacion && cotizacion > 0 ? (
        <>
          , al dólar de ese día (
          <span className="tabular-nums">$ {textoCotizacion(cotizacion)}</span>)
        </>
      ) : null}
      .
    </p>
  );
}

function variantePorTipo(tipo: string): "secondary" | "success" | "warning" | "info" {
  if (tipo === "COBRO" || tipo === "NC" || tipo === "AJUSTE+") return "success";
  if (tipo === "SALDO_INICIAL") return "info";
  if (tipo === "AJUSTE-") return "warning";
  return "secondary";
}
